import { useMutation, useQueryClient } from '@tanstack/react-query';

import { markFeatureSeen } from 'api/jalapeno';
import useAccountQuery from 'api/queries/useAccountQuery';
import { FEATURE_RECORD_QUERY_KEY } from 'api/queries/useFeatureRecordQuery';
import logError from 'utils/errorUtils';

/**
 * Hook for marking a feature as seen by the user.
 *
 * Pass `{ includeChargeId: true }` as the second argument to `markAsSeen` when the feature is
 * scoped to the **open shopping window** (same `nextOrder.chargeID` as the feature-record GET).
 * The POST body will then include `chargeID` when the account has one. Omit for **subscription
 * lifetime** features (e.g. onboarding).
 *
 * @returns {{ markAsSeen: (featureKey: string, mutationOptions?: Object) => void }}
 */
const useMarkFeatureSeen = () => {
    const queryClient = useQueryClient();
    const { data: account } = useAccountQuery();
    const subscriptionID = account?.subID;
    const chargeID = account?.nextOrder?.chargeID;
    const featureRecordQueryKey = [FEATURE_RECORD_QUERY_KEY, subscriptionID, chargeID];

    const mutation = useMutation({
        mutationFn: async ({ featureKey, includeChargeId }) => {
            if (!subscriptionID) {
                throw new Error('Missing subscription ID');
            }
            const sendParams = { feature: featureKey };
            if (includeChargeId && chargeID != null && chargeID !== '') {
                sendParams.chargeID = chargeID;
            }
            return markFeatureSeen(subscriptionID, sendParams);
        },
        onMutate: ({ featureKey }) => {
            // Abort in-flight feature-record fetches before updating cache. Otherwise a refetch
            // that resolves after this handler starts could overwrite the seen marker with stale
            // data. Do not await (avoids a render gap where the modal effect reopens).
            // revert: false + silent: true avoids reverting to pre-fetch unseen state or error UI.
            queryClient.cancelQueries({ queryKey: featureRecordQueryKey }, { revert: false, silent: true });

            const previousFeatureRecord = queryClient.getQueryData(featureRecordQueryKey);
            queryClient.setQueryData(featureRecordQueryKey, (old) => {
                if (!old || typeof old !== 'object') return old;
                return {
                    ...old,
                    [featureKey]: new Date().toISOString(),
                };
            });

            return { previousFeatureRecord };
        },
        onSuccess: () => {
            // Invalidate to ensure we have the server's actual value
            queryClient.invalidateQueries({ queryKey: [FEATURE_RECORD_QUERY_KEY] });
        },
        onError: (err, variables, context) => {
            // Rollback to previous value on error
            if (context?.previousFeatureRecord) {
                queryClient.setQueryData(featureRecordQueryKey, context.previousFeatureRecord);
            }
            logError(err);
            console.error('Error marking feature as seen:', err);
        },
    });

    /**
     * Mark a feature as seen.
     * @param {string} featureKey - The key identifying the feature being marked as seen.
     * @param {Object} [mutationOptions] - TanStack Query mutation callbacks (`onError`, `onSettled`, …).
     *   Set `includeChargeId: true` for open-window–scoped features so the POST includes `chargeID`
     *   when available (see hook file-level note). `includeChargeId` is not forwarded to TanStack.
     */
    const markAsSeen = (featureKey, mutationOptions = {}) => {
        const { includeChargeId, ...tanstackMutationOptions } = mutationOptions;
        mutation.mutate({ featureKey, includeChargeId }, tanstackMutationOptions);
    };

    return { markAsSeen };
};

export default useMarkFeatureSeen;
